import { Loading, Typography } from "@lyfegen-platform/react-common";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Collapse, Divider, useTheme } from "@material-ui/core";
import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from "@material-ui/icons";
import { Fragment, MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useRecoilValue, useSetRecoilState } from "recoil";
import LyfeTooltip from "../../../components/LyfeTooltip";
import { useDataMutation } from "../../../hooks/useDataModule";
import { DynamicFlexEntity } from "../../../models/flexible/flex-entity-schema";
import { TypedNegotiationRound } from "../../../models/flexible/typed-negotiation-round";
import { AgreementUserRole, Case, CaseStatus, NegotiationRoundStatus } from "../../../models/generated/graphql";
import { flexibleFormResponseState } from "../../../store/flexible";
import { userPersonalInfoState } from "../../../store/user";
import { GET_CASE_BY_ID, UPDATE_NEGOTIATION_ROUND } from "../queries";
import { UpdateNegotiationRound } from "../types/UpdateNegotiationRound";
import useStyles from "../use-styles";
import Negotiations, { INegotiationRoundModel } from "./Negotiations";

interface CaseReviewProps {
	caseInstance: Case;
	organizationId: string;
	role: AgreementUserRole | null | undefined;
	negotiationRounds: Array<TypedNegotiationRound>;
}

function ActionButtons(props: {
	model: INegotiationRoundModel;
	organizationId: string;
	role: AgreementUserRole | null | undefined;
	mutation: (args: { variables: any }) => void;
	caseId: string;
	loading: boolean;
	disabled: boolean;
	disabledAcceptWithoutReview: boolean;
}) {
	const { t } = useTranslation();
	const user = useRecoilValue(userPersonalInfoState);
	const [clickedButton, setClickedButton] = useState<NegotiationRoundStatus | null>(null);

	function handleAction(evt: MouseEvent<any, any>, status: NegotiationRoundStatus) {
		evt.preventDefault();
		evt.stopPropagation();
		setClickedButton(evt.currentTarget.id);
		props.mutation({
			variables: {
				input: {
					caseId: props.caseId,
					data: JSON.stringify({
						...props.model.source.data,
						data: props.model.data,
					}),
					id: props.model.source.id,
					status,
				},
			},
		});
	}

	if (props.model.source.status !== NegotiationRoundStatus.InReview) {
		return <></>;
	}

	const isActionAvailableByRole =
		props.role === AgreementUserRole.Editor || props.role === AgreementUserRole.Owner || props.role === AgreementUserRole.Approver;

	return (
		<Box display="flex">
			{props.organizationId === user.organization.externalId &&
				props.model.source.proposedBy.id === user.organization.externalId &&
				isActionAvailableByRole &&
				props.model.isValid &&
				!props.disabledAcceptWithoutReview &&
				!props.disabled && (
					<LyfeTooltip title={isActionAvailableByRole ? "" : t("case-review-buttons")}>
						<Box>
							<Button
								id={NegotiationRoundStatus.AcceptedWithoutReview}
								variant="outlined"
								color="primary"
								style={{ marginRight: "20px" }}
								onClick={(evt) => handleAction(evt, NegotiationRoundStatus.AcceptedWithoutReview)}
								startIcon={
									clickedButton === NegotiationRoundStatus.AcceptedWithoutReview &&
									props.loading && <Loading size="14px" color="inherit" />
								}
							>
								{t("proceed-without-review")}
							</Button>
						</Box>
					</LyfeTooltip>
				)}
			{props.model.source.proposedBy.id !== user.organization.externalId && (
				<>
					<LyfeTooltip title={isActionAvailableByRole ? "" : t("case-review-buttons")}>
						<Box>
							<Button
								id={NegotiationRoundStatus.Rejected}
								variant="outlined"
								color="primary"
								disabled={props.model.isChanged || !isActionAvailableByRole || props.disabled}
								style={{ marginRight: "20px" }}
								onClick={(evt) => handleAction(evt, NegotiationRoundStatus.Rejected)}
								startIcon={
									clickedButton === NegotiationRoundStatus.Rejected &&
									props.loading && <Loading size="14px" color="inherit" />
								}
							>
								{t("reject")}
							</Button>
						</Box>
					</LyfeTooltip>
					{!props.model.isChanged && (
						<LyfeTooltip title={isActionAvailableByRole ? "" : t("case-review-buttons")}>
							<Box>
								<Button
									id={NegotiationRoundStatus.Accepted}
									variant="contained"
									color="primary"
									disabled={!isActionAvailableByRole || props.disabled}
									style={{ marginRight: "20px" }}
									onClick={(evt) => handleAction(evt, NegotiationRoundStatus.Accepted)}
									startIcon={
										clickedButton === NegotiationRoundStatus.Accepted &&
										props.loading && <Loading size="14px" color="inherit" />
									}
								>
									{t("accept")}
								</Button>
							</Box>
						</LyfeTooltip>
					)}
				</>
			)}
			{(props.model.source.proposedBy.id === user.organization.externalId || props.model.isChanged) && (
				<LyfeTooltip title={isActionAvailableByRole ? "" : t("case-review-buttons")}>
					<Box>
						<Button
							id={NegotiationRoundStatus.InReview}
							variant="contained"
							disabled={!props.model.isChanged || !isActionAvailableByRole || !props.model.isValid || props.disabled}
							color="primary"
							style={{ marginRight: "20px" }}
							onClick={(evt) => handleAction(evt, NegotiationRoundStatus.InReview)}
							startIcon={
								clickedButton === NegotiationRoundStatus.InReview &&
								props.loading && <Loading size="14px" color="inherit" />
							}
						>
							{t("propose")}
						</Button>
					</Box>
				</LyfeTooltip>
			)}
		</Box>
	);
}

const CaseReview = (props: CaseReviewProps) => {
	const { push } = useHistory();
	const setGlobalResponse = useSetRecoilState(flexibleFormResponseState);
	const [isExpanded, setIsExpanded] = useState(props.caseInstance.status !== CaseStatus.Active);
	const { bodyItemHeaderTop } = useStyles();
	const { palette } = useTheme();
	const { t } = useTranslation();
	const firstPendingRoundIndex = props.negotiationRounds.findIndex((round) => round.status === NegotiationRoundStatus.InReview);
	const [models, setRoundProps] = useState<Array<INegotiationRoundModel>>([]);

	useEffect(() => {
		setRoundProps(
			props.negotiationRounds.map((round, index, rounds) => ({
				source: round,
				data: round.data,
				isChanged: false,
				isValid: true,
				isSelected: firstPendingRoundIndex >= 0 ? index === firstPendingRoundIndex : index === rounds.length - 1,
			}))
		);
	}, [props.negotiationRounds]);

	const [mutation, { loading }] = useDataMutation<UpdateNegotiationRound>(UPDATE_NEGOTIATION_ROUND, {
		onCompleted: (result) => {
			if ((result.UpdateNegotiationRound.case.status as unknown as CaseStatus) === CaseStatus.Abandoned) {
				push("/cases");
				return;
			}

			const selectedRound = getSelectedRound();
			updateById(selectedRound.source.id, {
				isChanged: false,
				source: {
					...result.UpdateNegotiationRound,
					source: selectedRound.source.source,
					data: JSON.parse(result.UpdateNegotiationRound.data) as DynamicFlexEntity,
				} as unknown as TypedNegotiationRound,
			});
		},
		refetchQueries: [{ query: GET_CASE_BY_ID, variables: { id: props.caseInstance.id } }],
	});
	const disableAcceptedWithoutReview = !!models.filter((model) => model.source.status === NegotiationRoundStatus.Accepted).length;

	useEffect(() => {
		setGlobalResponse(undefined);
	}, [setGlobalResponse]);

	if (!models.length) {
		return <></>;
	}

	function getSelectedRound() {
		return models.find((roundProps) => roundProps.isSelected)!;
	}

	function updateById(id: string, update: Partial<INegotiationRoundModel>) {
		setRoundProps(
			models.map((roundProps) =>
				roundProps.source.id === id
					? {
							...roundProps,
							...update,
					  }
					: roundProps
			)
		);
	}

	function handleChange() {
		setIsExpanded(!isExpanded);
	}

	function handleSelect(id: string) {
		setRoundProps(
			models.map((roundProps) => ({
				...roundProps,
				isSelected: roundProps.source.id === id,
			}))
		);
	}

	function handleChanged(id: string, isChanged: boolean, data: any, isValid: boolean) {
		updateById(id, { isChanged, data, isValid });
	}

	const selectedRoundIndex = models.findIndex((model) => model.isSelected);
	const isPreviousPendingRound = models.some(
		(model, index) => index < selectedRoundIndex && model.source.status === NegotiationRoundStatus.InReview
	);

	return (
		<Fragment>
			<Accordion
				expanded={true}
				defaultExpanded
				onChange={handleChange}
				TransitionProps={{ unmountOnExit: true }}
				style={{ borderRadius: "4px 4px 0px 0px", marginTop: 0 }}
				elevation={0}
			>
				<AccordionSummary
					className={bodyItemHeaderTop}
					expandIcon={isExpanded ? <ExpandMoreIcon color={"inherit"} /> : <ExpandLessIcon color={"inherit"} />}
					aria-controls={t("case-review")}
					id="case-review"
				>
					<Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
						<Typography type={"h5"} color={"textSecondary"}>
							{t("case-review")}
						</Typography>
						<ActionButtons
							caseId={props.caseInstance.id}
							model={getSelectedRound()}
							organizationId={props.organizationId}
							role={props.role}
							mutation={mutation}
							loading={loading}
							disabled={isPreviousPendingRound}
							disabledAcceptWithoutReview={disableAcceptedWithoutReview}
						/>
					</Box>
				</AccordionSummary>
				<Box justifyContent="space-between" width="100%" display="flex" alignItems="center" pb={2}>
					<Divider style={{ flexGrow: 1, backgroundColor: palette.tableDivider.main }} />
				</Box>
				<AccordionDetails>
					<Box display="flex" width="100%" flexDirection="column">
						<Collapse in={isExpanded}>
							<Negotiations
								handleSelect={handleSelect}
								handleChanges={handleChanged}
								roundModels={models}
								caseInstance={props.caseInstance}
							/>
						</Collapse>
					</Box>
				</AccordionDetails>
			</Accordion>
		</Fragment>
	);
};

export default CaseReview;
